import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import {
  Loader2, Plus, Copy, RefreshCw, ShieldCheck, ShieldAlert,
  UserCircle2, Tag as TagIcon, Calendar as CalendarIcon, Hash, Pencil,
  Search, Filter, MessageSquare, Radio,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Visual config per action type — icon, soft bg, border, label for filters.
// Kept as a single map so adding a new action_type only touches one place.
const ACTION_VISUALS = {
  POST_CREATED:                   { Icon: Plus,         tone: 'green',  label: 'Post Created' },
  POST_DUPLICATED:                { Icon: Copy,          tone: 'green',  label: 'Post Duplicated' },
  POST_UPDATED:                    { Icon: Pencil,       tone: 'slate',  label: 'Post Updated' },
  STATUS_CHANGED:                  { Icon: RefreshCw,    tone: 'blue',   label: 'Status Changed' },
  ASSIGNED_CHANGED:                { Icon: UserCircle2,  tone: 'blue',   label: 'Assignment Changed' },
  APPROVAL_CHANGED:                { Icon: ShieldCheck,  tone: 'green',  label: 'Approval Changed' },
  SCHEDULED_DATE_CHANGED:          { Icon: CalendarIcon, tone: 'blue',   label: 'Scheduled Date Changed' },
  POSTED_DATE_CHANGED:             { Icon: CalendarIcon, tone: 'blue',   label: 'Posted Date Changed' },
  CONTENT_RECEIVED_DATE_CHANGED:   { Icon: CalendarIcon, tone: 'blue',   label: 'Content Received Date Changed' },
  ACCOUNT_CHANGED:                 { Icon: TagIcon,      tone: 'blue',   label: 'Account Changed' },
  COLLEGE_CHANGED:                 { Icon: TagIcon,      tone: 'blue',   label: 'College Changed' },
  CONTENT_TYPE_CHANGED:            { Icon: Hash,          tone: 'blue',   label: 'Content Type Changed' },
  PLATFORMS_CHANGED:               { Icon: Radio,         tone: 'blue',   label: 'Platforms Changed' },
  COMMENT_ADDED:                   { Icon: MessageSquare, tone: 'slate',  label: 'Comment Added' },
};

const TONE_CLASSES = {
  green:  { ring: 'bg-green-100 text-green-700 ring-green-200' },
  blue:   { ring: 'bg-blue-100 text-blue-700 ring-blue-200' },
  red:    { ring: 'bg-red-100 text-red-700 ring-red-200' },
  slate:  { ring: 'bg-slate-100 text-slate-700 ring-slate-200' },
};

const renderTimestamp = (iso) => {
  try {
    return format(parseISO(iso), 'MMM d, yyyy • h:mm a');
  } catch (_) {
    return iso || '';
  }
};

// Replace user_id references in description with full names where possible
const hydrateAssigneeNames = (event, usersById) => {
  if (event.action_type !== 'ASSIGNED_CHANGED') return event;
  const oldName = usersById.get(event.old_value)?.full_name;
  const newName = usersById.get(event.new_value)?.full_name;
  let description = event.description || '';
  if (event.old_value && oldName) {
    description = description.split(event.old_value).join(oldName);
  }
  if (event.new_value && newName) {
    description = description.split(event.new_value).join(newName);
  }
  return {
    ...event,
    description,
    old_value: oldName || event.old_value || '—',
    new_value: newName || event.new_value || '—',
  };
};

// Pretty-print ISO timestamps that come through as raw strings inside diffs
const prettifyMaybeDate = (val) => {
  if (!val || typeof val !== 'string') return val;
  if (/^\d{4}-\d{2}-\d{2}T/.test(val)) {
    try { return format(parseISO(val), 'MMM d, yyyy'); } catch (_) { return val; }
  }
  return val;
};

const PostTimeline = ({ postId, users = [] }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionTypeFilter, setActionTypeFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [search, setSearch] = useState('');

  const usersById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const token = (() => {
    try { return localStorage.getItem('token'); } catch (_) { return null; }
  })();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!postId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/social-media/posts/${postId}/activity`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setEvents(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load activity');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [postId, token]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events
      .filter((e) => actionTypeFilter === 'all' || e.action_type === actionTypeFilter)
      .filter((e) => userFilter === 'all' || e.user_id === userFilter)
      .filter((e) => !q || (e.description || '').toLowerCase().includes(q))
      .map((e) => hydrateAssigneeNames(e, usersById));
  }, [events, actionTypeFilter, userFilter, search, usersById]);

  const uniqueActors = useMemo(() => {
    const seen = new Map();
    events.forEach((e) => {
      if (e.user_id && !seen.has(e.user_id)) seen.set(e.user_id, e.user_name || 'Unknown');
    });
    return [...seen.entries()];
  }, [events]);

  const availableActionTypes = useMemo(() => {
    const set = new Set(events.map((e) => e.action_type));
    return [...set];
  }, [events]);

  return (
    <div className="space-y-4" data-testid="post-timeline">
      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search activity..."
            className="h-9 pl-8 text-sm"
            data-testid="post-timeline-search"
          />
        </div>
        <Select value={actionTypeFilter} onValueChange={setActionTypeFilter}>
          <SelectTrigger className="h-9 w-[190px] text-sm" data-testid="post-timeline-action-filter">
            <Filter size={13} className="mr-1 text-slate-500" />
            <SelectValue placeholder="All Activities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Activities</SelectItem>
            {availableActionTypes.map((t) => (
              <SelectItem key={t} value={t}>{ACTION_VISUALS[t]?.label || t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger className="h-9 w-[160px] text-sm" data-testid="post-timeline-user-filter">
            <SelectValue placeholder="All Users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            {uniqueActors.map(([id, name]) => (
              <SelectItem key={id} value={id}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Feed */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-500" data-testid="post-timeline-loading">
          <Loader2 className="animate-spin mr-2" size={16} /> Loading activity…
        </div>
      ) : error ? (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 text-center text-sm text-slate-500">
          {events.length === 0 ? 'No activity logged yet.' : 'No activity matches your filters.'}
        </div>
      ) : (
        <ol className="relative pl-7 space-y-3" data-testid="post-timeline-feed">
          <span aria-hidden="true" className="absolute left-3 top-2 bottom-2 w-px bg-slate-200" />
          {filtered.map((event) => {
            const cfg = ACTION_VISUALS[event.action_type] || ACTION_VISUALS.POST_UPDATED;
            const tone = TONE_CLASSES[cfg.tone] || TONE_CLASSES.slate;
            const Icon = cfg.Icon;
            const showDiff = event.old_value || event.new_value;
            return (
              <li key={event.id} className="relative" data-testid={`post-timeline-event-${event.action_type}`}>
                <span
                  aria-hidden="true"
                  className={`absolute -left-7 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full ring-2 ring-white ${tone.ring}`}
                >
                  <Icon size={12} />
                </span>
                <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm hover:border-slate-300 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-sm font-medium text-slate-800 break-words">
                      {event.description}
                      {event.user_role && (
                        <span
                          className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200"
                          data-testid={`post-timeline-role-${event.action_type}`}
                        >
                          {event.user_role}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] whitespace-nowrap text-slate-400">
                      {renderTimestamp(event.created_at)}
                    </div>
                  </div>
                  {showDiff && !['POST_CREATED', 'POST_DUPLICATED', 'COMMENT_ADDED'].includes(event.action_type) && (
                    <div className="mt-1.5 text-xs text-slate-600">
                      <span className="line-through opacity-60">{prettifyMaybeDate(event.old_value) || '—'}</span>
                      <span className="mx-1.5 text-slate-400">→</span>
                      <span className={`font-medium ${tone.ring} px-1.5 py-0.5 rounded`}>{prettifyMaybeDate(event.new_value) || '—'}</span>
                    </div>
                  )}
                  {(event.action_type === 'POST_CREATED' || event.action_type === 'POST_DUPLICATED') && event.metadata && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {Object.entries(event.metadata)
                        .filter(([, v]) => v)
                        .map(([k, v]) => {
                          const display = k === 'assigned_to' ? (usersById.get(v)?.full_name || v) : v;
                          return (
                            <span key={k} className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200">
                              {k.replace(/_/g, ' ')}: {display}
                            </span>
                          );
                        })}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default PostTimeline;
